import type { ReactNode } from 'react';

import { cn } from '@/lib/utils/cn';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  tone?: 'neutral' | 'error';
}

/**
 * État vide.
 *
 * Composant dédié plutôt qu'un « Aucun résultat » posé dans chaque liste :
 * un état vide doit dire *pourquoi* c'est vide et *quoi faire ensuite*. Un
 * tableau vide sans explication laisse l'utilisateur se demander si les
 * données manquent ou si l'application est en panne.
 */
export const EmptyState = ({
  icon,
  title,
  description,
  action,
  className,
  tone = 'neutral',
}: EmptyStateProps) => (
  <div
    role={tone === 'error' ? 'alert' : undefined}
    className={cn('flex flex-col items-center px-6 py-14 text-center', className)}
  >
    {icon ? (
      <span
        aria-hidden
        className={cn(
          'mb-5 grid size-14 place-items-center rounded-[10px] border bg-linear-to-b from-white/80 to-transparent text-2xl shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),0_3px_8px_-3px_rgb(15_42_95/0.1)]',
          tone === 'error'
            ? 'border-red-200/70 bg-red-50 text-red-600'
            : 'border-navy-200/70 bg-navy-50 text-navy-500',
        )}
      >
        {icon}
      </span>
    ) : null}

    <p className="text-navy-900 text-[15px] font-semibold">{title}</p>

    {description ? (
      <p className="text-ink-500 mt-1.5 max-w-sm text-sm leading-relaxed">{description}</p>
    ) : null}

    {action ? <div className="mt-5">{action}</div> : null}
  </div>
);
